import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getGalleryItems, getTaxonomyTerms } from '../lib/cms';
import { Image as ImageIcon, ArrowRight, Phone } from 'lucide-react';

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Galleri & Referencer | Se Vores Udførte Byggeprojekter | Toa Byg ApS";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Udforsk billeder af vores udførte projekter hos Toa Byg ApS. Se eksempler på tagrenovering, asbestnedtagning, tømrerarbejde og renoveringsopgaver på Sjælland.');

    async function loadGalleryData() {
      try {
        const [galleryData, termsData] = await Promise.all([
          getGalleryItems(),
          getTaxonomyTerms('gallery-category'),
        ]);
        setItems(galleryData);
        setCategories(termsData);
      } catch (err) {
        console.error('Fejl ved indlæsning af galleri-data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadGalleryData();
  }, []);

  // Filtrer elementer baseret på valgt kategori
  const filteredItems = selectedCategory === 'alle'
    ? items
    : items.filter(item => item.terms?.['gallery-category']?.includes(selectedCategory));

  // Fladgør alle billeder på tværs af de filtrerede galleri-elementer
  const displayImages = [];
  filteredItems.forEach((item) => {
    if (item.image && Array.isArray(item.image)) {
      item.image.forEach((imgUrl, idx) => {
        displayImages.push({
          id: `${item.id}-${idx}`,
          slug: item.slug?._ || item.slug,
          title: item.title,
          imageUrl: imgUrl,
        });
      });
    }
  });

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header Banner */}
      <section className="bg-blue-950 text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">Vores Galleri</h1>
          <p className="mt-4 text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Se billeder af vores tidligere projekter inden for tagarbejde, tømrerarbejde og renovering i Ishøj og omegn.
          </p>
        </div>
      </section>

      {/* Filter & Galleri Grid */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Bar */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <button
              onClick={() => setSelectedCategory('alle')}
              className={`px-5 py-2.5 rounded-none text-sm font-semibold transition-all uppercase tracking-wider cursor-pointer ${
                selectedCategory === 'alle'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              Alle projekter
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`px-5 py-2.5 rounded-none text-sm font-semibold transition-all uppercase tracking-wider cursor-pointer ${
                  selectedCategory === cat.slug
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : displayImages.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-none border border-slate-100 shadow-sm">
              <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 uppercase">Ingen billeder fundet</h3>
              <p className="text-slate-500 mt-1">Der er i øjeblikket ingen billeder i denne kategori.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayImages.map((item) => {
                // Optimer billedet til kvadratisk format (600x600px)
                const optimizedUrl = item.imageUrl && item.imageUrl.startsWith('https://assets.auxodev.dk/')
                  ? item.imageUrl.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=80,format=auto/')
                  : item.imageUrl;

                return (
                  <Link
                    key={item.id}
                    to={`/galleri/${item.slug}`}
                    className="group relative rounded-none overflow-hidden shadow-md aspect-square bg-slate-100 block"
                  >
                    <img
                      src={optimizedUrl}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {/* Elegant hover overlay med projektets titel */}
                    <div className="absolute inset-0 bg-blue-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <div className="text-white">
                        <p className="text-xs font-bold uppercase tracking-wider text-blue-300 mb-1">Se projekt</p>
                        <h3 className="text-lg font-extrabold uppercase tracking-tight">{item.title}</h3>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Kontakt CTA Sektion */}
      <section className="py-16 lg:py-20 bg-blue-900 text-white text-center border-t border-blue-850">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight uppercase">Inspireret af vores tidligere arbejde?</h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Lad os tage en uforpligtende snak om, hvordan vi kan hjælpe dig med at realisere dit næste byggeprojekt.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-base font-semibold rounded-none text-blue-900 bg-white hover:bg-blue-50 transition-colors shadow-md uppercase tracking-wider"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:+4540942420"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-blue-400 text-base font-semibold rounded-none text-white bg-transparent hover:bg-blue-800 transition-colors uppercase tracking-wider"
            >
              <Phone className="h-5 w-5 text-blue-300" />
              Ring +45 40 94 24 20
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
